// File: src/pages/OfferingDegrees.js
// Description: Offering Degrees page component displaying available degree programs and requirements

import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/pages/offering-degrees.css';

const OfferingDegrees = () => {
  const { getText, isMyanmar } = useLanguage();

  // Degree programs data with bilingual support
  const degreePrograms = [
    {
      id: 1,
      title: {
        en: 'Pre-Diploma Course',
        my: 'အကြိုဒီပလိုမာသင်တန်း'
      },
      duration: {
        en: '2 Years',
        my: '(၂)နှစ်'
      },
      description: {
        en: 'Preparatory course for Dhamma-Vinaya studies',
        my: 'ဓမ္မ-ဝိနယ လေ့လာမှုအတွက် အကြိုပြင်ဆင်သင်တန်း'
      }
    },
    {
      id: 2,
      title: {
        en: 'Diploma in Dhamma-Vinaya',
        my: 'ဘွဲ့ကြိုဒီပလိုမာသင်တန်း'
      },
      duration: {
        en: '1 Year',
        my: '(၁) နှစ်'
      },
      description: {
        en: 'Foundation diploma in Buddhist teachings and monastic discipline',
        my: 'ဗုဒ္ဓဘာသာ ဆုံးမများနှင့် ရဟန်းပြဋ္ဌာန်းချက်များ အခြေခံ ဒီပလိုမာ'
      }
    },
    {
      id: 3,
      title: {
        en: 'B.A in Dhamma-Vinaya',
        my: 'ဝိဇ္ဇာဘွဲ့'
      },
      duration: {
        en: '2 Years',
        my: '(၂) နှစ်'
      },
      description: {
        en: 'Bachelor\'s degree in Buddhist philosophy and ethics',
        my: 'ဗုဒ္ဓဒဿန နှင့် ကျင့်ဝတ်ပညာ ဘွဲ့တန်း'
      }
    },
    {
      id: 4,
      title: {
        en: 'M.A in Dhamma-Vinaya',
        my: 'မဟာဝိဇ္ဇာဘွဲ့ ဓမ္မ-ဝိနယ'
      },
      duration: {
        en: '2 Years',
        my: '(၂)နှစ်'
      },
      description: {
        en: 'Master\'s degree in advanced Buddhist studies',
        my: 'အဆင့်မြင့် ဗုဒ္ဓဘာသာ လေ့လာမှု မဟာဘွဲ့'
      }
    },
    {
      id: 5,
      title: {
        en: 'Post Graduate Diploma in Pali-Sanskrit',
        my: 'ဘွဲ့လွန်ဒီပလိုမာသင်တန်း'
      },
      duration: {
        en: '1 Year',
        my: '(၁)နှစ်'
      },
      description: {
        en: 'Advanced study of Pali and Sanskrit languages',
        my: 'ပါဠိ နှင့် သက္ကတ ဘာသာစကား အဆင့်မြင့် လေ့လာမှု'
      }
    },
    {
      id: 6,
      title: {
        en: 'M.A in Pali-Sanskrit',
        my: 'မဟာဝိဇ္ဇာဘွဲ့ ပါဠိ-သက္ကတ'
      },
      duration: {
        en: '2 Years',
        my: '(၂) နှစ်'
      },
      description: {
        en: 'Master\'s degree in Pali and Sanskrit studies',
        my: 'ပါဠိ နှင့် သက္ကတ လေ့လာမှု မဟာဘွဲ့'
      }
    },
    {
      id: 7,
      title: {
        en: 'B.A (University Dhammacariya)',
        my: 'တက္ကသီလ ဓမ္မာစရိယ'
      },
      duration: {
        en: '4 Years',
        my: '(၄) နှစ်'
      },
      description: {
        en: 'Bachelor\'s degree with specialization in Dhamma teaching',
        my: 'ဓမ္မဆုံးမ အထူးပြု ဘွဲ့တန်း'
      }
    }
  ];

  // General requirements data
  const generalRequirements = {
    title: {
      en: 'General Requirements for Students',
      my: 'သင်တန်းသားအရည်အချင်း'
    },
    requirements: [
      {
        en: 'Age limit: 30 years, with 10 years of monastic life (exceptions may be made for those with exceptional faith)',
        my: 'အသက်(၃၀) ၊ သိက္ခာတော်(၁၀)ဝါ အတွင်းဖြစ်ရပါမည်။(သဒ္ဓါတရားလွန်စွာထက်သန်သူများ အတွက် အသက်ကိုခြွင်းချက်ထားရှိပါမည်။)'
      },
      {
        en: 'Must highly respect and follow the Vinaya rules. Must be able to recite the Bhikkhu Patimokkha',
        my: 'ဝိနည်းတော်ကို အထူးမြတ်နိုးလိုက်နာနိုင်သူဖြစ်ရပါမည်။ ဘိက္ခုပါတိမောက် တစ်စောင် အာဂုံဆောင်နိုင်သူဖြစ်ရမည်။'
      },
      {
        en: 'Must not be a person who causes division in the community',
        my: 'ပရိသဒူသက ပုဂ္ဂိုလ်မျိုး မဖြစ်စေရ။'
      },
      {
        en: 'Must be residential students (monks and novices)',
        my: 'အဆောင်နေ စာသင်သား ရဟန်း သာမဏေများ ဖြစ်ရမည်။'
      }
    ]
  };

  // Specific requirements for each program
  const specificRequirements = [
    {
      program: {
        en: 'Pre-Diploma in Dhamma Vinaya',
        my: 'Pre-Diploma in Dhamma Vinaya သင်တန်း'
      },
      requirements: {
        en: 'Must pass middle school level, pass Samanera-gyi Second Level, or pass university entrance exam',
        my: 'အလတ်တန်း တန်းအောင်မြင်ရမည်၊သာမဏေကျော် ဒုတိယအဆင့် အောင်မြင်သူဖြစ်ရမည်၊ ပြင်ပပညာရေး တွင် တက္ကသိုလ်ဝင်တန်းအောင်မြင်ပြီးသူဖြစ်ရမည်။'
      }
    },
    {
      program: {
        en: 'Diploma in Dhamma Vinaya',
        my: 'Diploma in Dhamma Vinaya သင်တန်း'
      },
      requirements: {
        en: 'Must have completed Pre-Diploma in Dhamma-Vinaya. Degree holders may take entrance exam directly without Pre-Diploma',
        my: 'Pre-Diploma in Dhamma-Vinay တန်းကို အောင်မြင်ထားသူဖြစ်ရမည်။ ဘွဲ့ရပြီးသော ပုဂ္ဂိုလ်များသည် Pre-Diploma အတန်းကို မတက်ရောက်ဘဲ Diploma အတန်းဝင်ခွင့်ကို ဖြေဆိုတက်ရောက်နိုင်သည်။'
      }
    },
    {
      program: {
        en: 'B.A in Dhamma-Vinaya',
        my: 'B.A in Dhamma-Vinaya (ဝိဇ္ဇာဘွဲ့-ဓမ္မဝိနယ) သင်တန်း'
      },
      requirements: {
        en: 'Must have obtained Diploma in Dhamma-Vinaya',
        my: 'Diploma in Dhamma – Vinay ရရှိသူ ဖြစ်ရမည်။'
      }
    },
    {
      program: {
        en: 'M.A in Dhamma-Vinaya',
        my: 'M.A in Dhamma-Vinay (မဟာဝိဇ္ဇာဘွဲ့-ဓမ္မဝိနယ) သင်တန်း'
      },
      requirements: {
        en: 'Must have obtained B.A in Dhamma-Vinaya degree',
        my: 'B.A in Dhamma-Vinaya ဘွဲ့ရရှိထားသူဖြစ်ရမည်။'
      }
    },
    {
      program: {
        en: 'Post Graduate Diploma in Pali-Sanskrit',
        my: 'Post Graduate Diploma in Pali-Sanskrit (ဘွဲ့လွန်ဒီပလိုမာ ပါဠိ-သက္ကတ) တန်း'
      },
      requirements: {
        en: 'Must have obtained a Dhammacariya degree (Government Dhammacariya, University Dhammacariya, or Dhammacariya from any organization) or a university degree related to Dhamma-Vinaya',
        my: 'ဓမ္မာစရိယ ဘွဲ့တခုရရမည် (ဓမ္မာစရိယဘွဲ့ ဆိုသည်မှာ အစိုးရဓမ္မစရိယ၊ တက္ကသိုလ်ဓမ္မာစရိယ၊ အသင်းခုခုမှဓမ္မာစရိယ)။ သို့မဟုတ် တက္ကသိုလ်မှ ဓမ္မဝိနယ ဆိုင်ရာ ဘွဲ့တစ်ခု ရရှိထားရမည်။'
      }
    },
    {
      program: {
        en: 'M.A in Pali-Sanskrit',
        my: 'M.A in Pali-Sanskrit (မဟာဝိဇ္ဇာဘွဲ့ ပါဠိ-သက္ကတ) တန်း'
      },
      requirements: {
        en: 'Must have completed and passed Diploma in Pali-Sanskrit',
        my: 'Diploma in Pali-Sanskrit တန်းသို့ တက်ရောက်အောင်မြင်ထားသူဖြစ်ရမည်။'
      }
    },
    {
      program: {
        en: 'B.A University Dhammacariya',
        my: 'B.A တက္ကသီလဓမ္မာစရိယ သင်တန်း'
      },
      requirements: {
        en: 'Must pass high school level or pass Samanera-gyi examination',
        my: 'အကြီးတန်း အောင်မြင်ရမည်။(သို့မဟုတ်) သာမဏေကျော် အောင်မြင်ထားသူဖြစ်ရမည်။'
      }
    }
  ];

  return (
    <div className="offering-degrees-page">
      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="banner-content">
            <h1 className={isMyanmar() ? 'myanmar-text' : 'english-text'}>
              {getText({
                en: 'Offering Degrees',
                my: 'သင်ကြားနိုင်သောဘွဲ့များ'
              })}
            </h1>
            <p className={isMyanmar() ? 'myanmar-text' : 'english-text'}>
              {getText({
                en: 'Academic Programs and Requirements',
                my: 'ပညာရေးအစီအစဉ်များနှင့် လိုအပ်ချက်များ'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="degrees-content">
        <div className="container">
          
          {/* Degree Programs Section */}
          <section className="degrees-section">
            <h2 className={`section-title ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
              {getText({
                en: 'Available Degree Programs',
                my: 'ရရှိနိုင်သော ဘွဲ့အစီအစဉ်များ'
              })}
            </h2>
            
            <div className="degrees-grid">
              {degreePrograms.map((program) => (
                <div key={program.id} className="degree-card">
                  <div className="degree-header">
                    <h3 className={`degree-title ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                      {getText(program.title)}
                    </h3>
                    <div className="degree-duration">
                      <span className={`duration-text ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                        {getText({
                          en: 'Duration: ',
                          my: 'သင်တန်းကာလ '
                        })}
                        {getText(program.duration)}
                      </span>
                    </div>
                  </div>
                  <div className="degree-body">
                    <p className={`degree-description ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                      {getText(program.description)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* General Requirements Section */}
          <section className="requirements-section">
            <h2 className={`section-title ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
              {getText(generalRequirements.title)}
            </h2>
            
            <div className="requirements-card">
              <p className={`requirements-intro ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                {getText({
                  en: 'Students wishing to attend the above courses must meet the following requirements:',
                  my: 'အထက်ပါသင်တန်းများသို့ တက်ရောက်လိုသူများသည်'
                })}
              </p>
              
              <ul className="requirements-list">
                {generalRequirements.requirements.map((requirement, index) => (
                  <li key={index} className={`requirement-item ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                    {getText({
                      en: requirement.en,
                      my: requirement.my
                    })}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Specific Requirements Section */}
          <section className="specific-requirements-section">
            <h2 className={`section-title ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
              {getText({
                en: 'Specific Program Requirements',
                my: 'သီးခြားအစီအစဉ် လိုအပ်ချက်များ'
              })}
            </h2>
            
            <div className="specific-requirements-grid">
              {specificRequirements.map((item, index) => (
                <div key={index} className="specific-requirement-card">
                  <h3 className={`program-name ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                    {getText(item.program)}
                  </h3>
                  <p className={`program-requirements ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                    {getText(item.requirements)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Information */}
          <section className="contact-section">
            <div className="contact-card">
              <h3 className={`contact-title ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                {getText({
                  en: 'For More Information',
                  my: 'နောက်ထပ် အချက်အလက်များအတွက်'
                })}
              </h3>
              <p className={`contact-text ${isMyanmar() ? 'myanmar-text' : 'english-text'}`}>
                {getText({
                  en: 'Please contact the Registrar Office for detailed information about admission procedures, application forms, and enrollment dates.',
                  my: 'ဝင်ခွင့်လုပ်ထုံးလုပ်နည်းများ၊ လျှောက်လွှာပုံစံများနှင့် စာရင်းသွင်းရက်များအကြောင်း အသေးစိတ်အချက်အလက်များအတွက် မှတ်ပုံတင်ရေးမှူးရုံးသို့ ဆက်သွယ်ပါ။'
                })}
              </p>
              <div className="contact-actions">
                <a href="/registrar" className="btn-primary">
                  <span className={isMyanmar() ? 'myanmar-text' : 'english-text'}>
                    {getText({
                      en: 'Contact Registrar',
                      my: 'မှတ်ပုံတင်ရေးမှူးနှင့် ဆက်သွယ်ရန်'
                    })}
                  </span>
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default OfferingDegrees;